import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SubscriptionContext = createContext({
  plan: null,
  planId: null,
  usage: {},
  subscriptionStatus: 'free',
  loading: true,
  refresh: async () => {},
  hasFeature: () => false,
  getRemaining: () => null,
});

export const SubscriptionProvider = ({ children }) => {
  const [data, setData] = useState({
    plan: null,
    planId: null,
    usage: {},
    subscriptionStatus: 'free',
    cancelAtPeriodEnd: false,
    currentPeriodEnd: null,
    trialEnd: null,
    userType: null,
  });
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      setLoading(false);
      return;
    }
    try {
      const res = await axios.get(`${API_URL}/api/subscription/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.data?.success) {
        setData({
          plan: res.data.plan,
          planId: res.data.plan_id,
          usage: res.data.usage || {},
          subscriptionStatus: res.data.subscription_status,
          cancelAtPeriodEnd: res.data.cancel_at_period_end,
          currentPeriodEnd: res.data.current_period_end,
          trialEnd: res.data.trial_end,
          userType: res.data.user_type,
        });
      }
    } catch (err) {
      // 401/403 = unauthenticated, expected for anonymous routes
      if (err.response?.status >= 500) {
        console.error('Failed to fetch subscription:', err);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
    // Refresh on token change (login/logout)
    const onStorage = (e) => { if (e.key === 'token') refresh(); };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [refresh]);

  // Boolean feature check (handles both snake_case and camelCase keys)
  const hasFeature = useCallback((feature) => {
    if (!data.plan) return false;
    if (data.plan._unlimited) return true;
    // Check both forms because backend uses snake_case
    return Boolean(data.plan[feature] ?? data.plan[snakeCase(feature)]);
  }, [data.plan]);

  // Returns {limit, used, remaining} for a usage counter
  const getRemaining = useCallback((counter) => {
    const u = data.usage || {};
    // The plan_limits.get_usage_stats output shape varies by user type:
    //   worker: { job_applications: { limit, used, remaining } }
    //   contractor: { job_postings: { limit, used, remaining } }
    return u[counter] || null;
  }, [data.usage]);

  return (
    <SubscriptionContext.Provider value={{
      ...data,
      loading,
      refresh,
      hasFeature,
      getRemaining,
    }}>
      {children}
    </SubscriptionContext.Provider>
  );
};

export const useSubscription = () => useContext(SubscriptionContext);

const snakeCase = (s) => s.replace(/([A-Z])/g, '_$1').toLowerCase();
